import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SuiteHeader from '../components/suite/SuiteHeader';
import SuiteSection from '../components/suite/SuiteSection';
import SeatCard from '../components/suite/SeatCard';
import JoeBealStory from '../components/suite/JoeBealStory';
import ProcessTimeline from '../components/suite/ProcessTimeline';
import {SEATS} from '../components/suite/content';

const {FiArrowRight, FiArrowUpRight, FiLock, FiUsers} = FiIcons;

const TrialQualifierPage = () => {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({behavior: 'smooth'});
  };

  const scrollToProcess = () => {
    document.getElementById('process')?.scrollIntoView({behavior: 'smooth'});
  };

  const applyForPlan = (seat) => {
    window.location.href = seat.bookingUrl;
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-serif text-black">
      <SuiteHeader onApply={scrollToPricing} onQualify={scrollToProcess} />

      <main>
        <section className="px-4 py-20 sm:px-8 md:py-28">
          <motion.div
            initial={{opacity: 0, y: 14}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.45}}
            className="mx-auto max-w-4xl"
          >
            <h1 className="max-w-4xl text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl">
              Somebody&apos;s calling your leads right now. Is it you?
            </h1>

            <p className="mt-8 max-w-3xl text-xl leading-relaxed text-neutral-700 sm:text-2xl">
              Because if it&apos;s not — it&apos;s someone else.
            </p>

            <div className="mt-8 max-w-3xl border-l-4 border-black bg-yellow-400 px-6 py-5">
              <p className="font-sans text-lg font-black uppercase leading-tight tracking-tight text-black sm:text-2xl">
                You built that list. Every name on it is a warm buyer you already paid for.
              </p>
            </div>

            <p className="mt-8 max-w-3xl text-lg leading-relaxed text-neutral-700">
              But you&apos;re busy doing the one thing you should be doing — closing deals. And every
              minute you&apos;re not on your list, somebody else is. Another dealership. Another rep.
              Another platform. While you&apos;re on a test drive, somebody is texting your customer
              back. Your list doesn&apos;t wait around. Neither do buyers.
            </p>

            <div className="mt-10 flex flex-col gap-4 font-sans sm:flex-row">
              <button
                onClick={scrollToPricing}
                className="flex items-center justify-center gap-3 bg-black px-7 py-4 text-sm font-black uppercase tracking-wide text-white transition hover:bg-neutral-800"
              >
                Put Your List To Work <SafeIcon icon={FiArrowUpRight} />
              </button>
              <button
                onClick={scrollToProcess}
                className="flex items-center justify-center gap-3 border border-black px-7 py-4 text-sm font-black uppercase tracking-wide text-black transition hover:bg-neutral-100"
              >
                See How It Works <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </motion.div>
        </section>

        <SuiteSection
          eyebrow="The problem"
          title="Your leads are not cold. They're just not being worked."
          className="border-y border-neutral-300 bg-neutral-50"
        >
          <div className="max-w-3xl space-y-6 text-lg leading-relaxed text-neutral-800">
            <p>
              The people in your database already know who you are. They already raised their hand.
              They just need someone to reach back out before another dealership does.
            </p>
            <p>
              So we built a team to make sure that never costs you another deal. We handle the
              follow-up your schedule cannot hold. When a buyer is ready, you get the conversation.
              Then you do what you do best: close.
            </p>
          </div>
        </SuiteSection>

        <div id="process">
          <ProcessTimeline />
        </div>

        <SuiteSection
          eyebrow="The math"
          title="No new ad spend. No new traffic. Just the leads you already paid for."
        >
          <div className="grid max-w-5xl gap-8 md:grid-cols-[1fr_1.2fr] md:items-center">
            <div className="flex h-28 w-28 items-center justify-center rounded-full bg-yellow-400 text-black shadow-[7px_7px_0_#000]">
              <SafeIcon icon={FiUsers} className="text-5xl" />
            </div>
            <div>
              <p className="text-2xl font-black leading-tight text-black sm:text-3xl">
                Average clients turn every 500 leads into 30–50 additional engaged, ready-to-buy
                opportunities.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-neutral-700">
                These are leads they already had, just sitting cold. Somebody&apos;s calling your
                leads right now. Make sure it&apos;s your team.
              </p>
            </div>
          </div>
        </SuiteSection>

        <JoeBealStory />

        <SuiteSection
          id="pricing"
          eyebrow="Choose your arrangement"
          title="Same team, same deliverables. Pick the commitment that fits."
          className="border-t border-neutral-300 bg-neutral-50"
        >
          <div className="grid gap-6 md:grid-cols-2">
            {SEATS.map((seat) => (
              <SeatCard key={seat.name} seat={seat} onApply={applyForPlan} />
            ))}
          </div>
          <p className="mx-auto mt-10 max-w-3xl text-center text-lg font-bold leading-relaxed text-neutral-800">
            One extra deal a month pays for the entire system. Everything after that is income from
            leads you already earned.
          </p>
        </SuiteSection>

        <section className="border-t border-black px-4 py-24 text-center sm:px-8 md:py-28">
          <div className="mx-auto max-w-3xl">
            <p className="mb-4 font-sans text-xs font-black uppercase tracking-[0.2em] text-neutral-600">
              The close
            </p>
            <h2 className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl">
              Make sure the next person calling your leads is on your team.
            </h2>
            <p className="mt-7 text-lg leading-relaxed text-neutral-700">
              We work your list. We re-engage the buyers who are ready. You get handed the
              opportunity and close the deal.
            </p>
            <button
              onClick={scrollToPricing}
              className="mt-10 inline-flex items-center gap-3 bg-black px-9 py-5 font-sans text-sm font-black uppercase tracking-wide text-white transition hover:bg-neutral-800"
            >
              Put Your List To Work <SafeIcon icon={FiArrowUpRight} />
            </button>
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-300 px-4 py-10 text-center sm:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-sm font-black uppercase tracking-[0.12em] text-black">
            Elite Sales &amp;Automation
          </p>
          <p className="mt-5 font-sans text-xs leading-relaxed text-neutral-500">
            Results are not typical and depend on your market, your close rate, and your
            follow-through. Elite Sales &amp;Automation manages listings, supports database
            reactivation, and provides follow-up support. It does not close deals for you and makes
            no guarantee of income, appointments, or results.
          </p>
          <p className="mt-6 flex items-center justify-center gap-2 font-sans text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
            <SafeIcon icon={FiLock} /> Application Only
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrialQualifierPage;